const DEFAULT_PRIMARY_COLOR = "#2563eb";
const DEFAULT_SECONDARY_COLOR = "#f8fafc";
const DEFAULT_TEXT_COLOR = "#0f172a";
const DEFAULT_FONT_PRESET = "moderna";

export const BRAND_FONT_OPTIONS = [
  {
    id: "moderna",
    label: "Moderna",
    family:
      'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: "elegante",
    label: "Elegante",
    family: '"Cormorant Garamond", "Times New Roman", Georgia, serif',
  },
  {
    id: "clasica",
    label: "Clasica",
    family: '"Libre Baskerville", Georgia, "Times New Roman", serif',
  },
  {
    id: "amigable",
    label: "Amigable",
    family: '"Nunito", "Trebuchet MS", "Segoe UI", sans-serif',
  },
  {
    id: "compacta",
    label: "Compacta",
    family: '"Barlow Semi Condensed", "Arial Narrow", "Segoe UI", sans-serif',
  },
];

export const BRAND_LOADING_ANIMATION_OPTIONS = [
  { id: "spin", label: "Giro suave" },
  { id: "pulse", label: "Pulso" },
  { id: "float", label: "Flotar" },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeHexColor = (value, fallback) => {
  if (typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  const normalized = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(normalized)) {
    return fallback;
  }

  if (normalized.length === 3) {
    return `#${normalized
      .split("")
      .map((char) => `${char}${char}`)
      .join("")
      .toLowerCase()}`;
  }

  return `#${normalized.toLowerCase()}`;
};

const hexToRgb = (value) => {
  const normalized = normalizeHexColor(value, "");
  if (!normalized) {
    return null;
  }

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) =>
      clamp(Math.round(channel), 0, 255).toString(16).padStart(2, "0")
    )
    .join("")}`;

const buildRoundedRectPath = (context, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);

  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
};

const mixColors = (baseColor, mixColor, ratio) => {
  const base = hexToRgb(baseColor);
  const mix = hexToRgb(mixColor);
  if (!base || !mix) {
    return normalizeHexColor(baseColor, DEFAULT_PRIMARY_COLOR);
  }

  const safeRatio = clamp(ratio, 0, 1);
  return rgbToHex({
    r: base.r + (mix.r - base.r) * safeRatio,
    g: base.g + (mix.g - base.g) * safeRatio,
    b: base.b + (mix.b - base.b) * safeRatio,
  });
};

const getPerceivedLuminance = (color) => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return 0.5;
  }

  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
};

export const getBrandTextColor = (backgroundColor) =>
  getPerceivedLuminance(backgroundColor) > 0.62
    ? DEFAULT_TEXT_COLOR
    : DEFAULT_SECONDARY_COLOR;

export const getBrandFontFamily = (fontPreset) =>
  BRAND_FONT_OPTIONS.find((option) => option.id === fontPreset)?.family ||
  BRAND_FONT_OPTIONS[0].family;

export const normalizeRestaurantBranding = (branding = {}) => {
  const primaryColor = normalizeHexColor(
    branding.primaryColor,
    normalizeHexColor(branding.suggestedPrimaryColor, DEFAULT_PRIMARY_COLOR)
  );
  const secondaryColor = normalizeHexColor(
    branding.secondaryColor,
    normalizeHexColor(branding.suggestedSecondaryColor, getBrandTextColor(primaryColor))
  );

  return {
    logoPrimaryUrl: branding.logoPrimaryUrl || "",
    logoCompactUrl: branding.logoCompactUrl || "",
    iconLogoUrl: branding.iconLogoUrl || "",
    loadingLogoUrl: branding.loadingLogoUrl || "",
    fontFamily:
      typeof branding.fontFamily === "string" ? branding.fontFamily.trim() : "",
    loadingAnimation:
      BRAND_LOADING_ANIMATION_OPTIONS.find(
        (option) => option.id === branding.loadingAnimation
      )?.id || "spin",
    suggestedPrimaryColor: normalizeHexColor(
      branding.suggestedPrimaryColor,
      primaryColor
    ),
    suggestedSecondaryColor: normalizeHexColor(
      branding.suggestedSecondaryColor,
      getBrandTextColor(primaryColor)
    ),
    primaryColor,
    secondaryColor,
    fontPreset:
      BRAND_FONT_OPTIONS.find((option) => option.id === branding.fontPreset)?.id ||
      DEFAULT_FONT_PRESET,
  };
};

export const buildRestaurantBrandTheme = (profile = {}) => {
  const branding = normalizeRestaurantBranding(profile.branding);
  const effectiveLogoCompactUrl =
    branding.logoCompactUrl || branding.logoPrimaryUrl || "";
  const effectiveIconLogoUrl =
    branding.iconLogoUrl || effectiveLogoCompactUrl || branding.logoPrimaryUrl || "";
  const effectiveLoadingLogoUrl =
    branding.loadingLogoUrl ||
    effectiveIconLogoUrl ||
    effectiveLogoCompactUrl ||
    branding.logoPrimaryUrl ||
    "";
  const primaryColor = branding.primaryColor;
  const secondaryColor = branding.secondaryColor;

  return {
    ...branding,
    effectiveLogoCompactUrl,
    effectiveIconLogoUrl,
    effectiveLoadingLogoUrl,
    primaryColor,
    secondaryColor,
    softColor: mixColors(primaryColor, "#ffffff", 0.9),
    pageTint: mixColors(primaryColor, "#f8fafc", 0.94),
    borderColor: mixColors(primaryColor, "#ffffff", 0.74),
    fontFamily: branding.fontFamily || getBrandFontFamily(branding.fontPreset),
    heroBackground:
      getPerceivedLuminance(primaryColor) > 0.55
        ? `linear-gradient(135deg, ${mixColors(
            primaryColor,
            "#ffffff",
            0.08
          )} 0%, ${primaryColor} 100%)`
        : `linear-gradient(135deg, ${mixColors(
            primaryColor,
            "#0f172a",
            0.1
          )} 0%, ${primaryColor} 100%)`,
  };
};

export const buildFaviconDataUrl = (
  imageUrl,
  {
    size = 64,
    paddingRatio = 0.08,
    backgroundColor = "",
    cornerRadiusRatio = 0.22,
  } = {}
) =>
  new Promise((resolve, reject) => {
    if (!imageUrl || typeof window === "undefined") {
      reject(new Error("No hay imagen para generar el icono."));
      return;
    }

    const image = new window.Image();
    image.decoding = "async";
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");

        if (!context) {
          reject(new Error("No se pudo preparar el icono de la pestana."));
          return;
        }

        context.clearRect(0, 0, size, size);

        if (backgroundColor) {
          buildRoundedRectPath(
            context,
            0,
            0,
            size,
            size,
            Math.round(size * cornerRadiusRatio)
          );
          context.fillStyle = backgroundColor;
          context.fill();
        }

        const imageRatio = image.width / Math.max(1, image.height);
        const effectivePaddingRatio = clamp(
          imageRatio > 1.35 || imageRatio < 0.74
            ? paddingRatio * 0.45
            : paddingRatio,
          0.02,
          0.16
        );
        const padding = Math.round(size * effectivePaddingRatio);
        const availableWidth = Math.max(1, size - padding * 2);
        const availableHeight = Math.max(1, size - padding * 2);
        const scale = Math.min(
          availableWidth / Math.max(1, image.width),
          availableHeight / Math.max(1, image.height)
        );
        const drawWidth = Math.max(1, Math.round(image.width * scale));
        const drawHeight = Math.max(1, Math.round(image.height * scale));
        const offsetX = Math.round((size - drawWidth) / 2);
        const offsetY = Math.round((size - drawHeight) / 2);

        context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => reject(new Error("No se pudo leer el icono seleccionado."));
    image.src = imageUrl;
  });

export const extractBrandPaletteFromImage = (imageUrl) =>
  new Promise((resolve, reject) => {
    if (!imageUrl || typeof window === "undefined") {
      reject(new Error("No hay imagen para analizar."));
      return;
    }

    const image = new window.Image();
    image.decoding = "async";
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        const sampleSize = 42;
        const canvas = document.createElement("canvas");
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        const context = canvas.getContext("2d", { willReadFrequently: true });

        if (!context) {
          reject(new Error("No se pudo preparar la muestra de color."));
          return;
        }

        context.drawImage(image, 0, 0, sampleSize, sampleSize);
        const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
        const buckets = new Map();

        for (let index = 0; index < data.length; index += 4) {
          const alpha = data[index + 3] / 255;
          if (alpha < 0.1) {
            continue;
          }

          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const brightness = (r + g + b) / 3 / 255;

          if (saturation < 0.14 || brightness > 0.95 || brightness < 0.12) {
            continue;
          }

          const key = [
            Math.round(r / 24) * 24,
            Math.round(g / 24) * 24,
            Math.round(b / 24) * 24,
          ].join("-");
          const current = buckets.get(key) || { score: 0, r: 0, g: 0, b: 0, count: 0 };

          current.score += saturation * 2 + (1 - Math.abs(brightness - 0.48));
          current.r += r;
          current.g += g;
          current.b += b;
          current.count += 1;
          buckets.set(key, current);
        }

        const top = [...buckets.values()].sort((left, right) => right.score - left.score)[0];

        if (!top || !top.count) {
          resolve({
            primaryColor: DEFAULT_PRIMARY_COLOR,
            secondaryColor: getBrandTextColor(DEFAULT_PRIMARY_COLOR),
          });
          return;
        }

        const primaryColor = rgbToHex({
          r: top.r / top.count,
          g: top.g / top.count,
          b: top.b / top.count,
        });

        resolve({
          primaryColor,
          secondaryColor: getBrandTextColor(primaryColor),
        });
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => reject(new Error("No se pudo leer el logo seleccionado."));
    image.src = imageUrl;
  });

export const extractImageBackdropColor = (imageUrl) =>
  new Promise((resolve, reject) => {
    if (!imageUrl || typeof window === "undefined") {
      reject(new Error("No hay imagen para analizar."));
      return;
    }

    const image = new window.Image();
    image.decoding = "async";
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        const sampleSize = 24;
        const canvas = document.createElement("canvas");
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        const context = canvas.getContext("2d", { willReadFrequently: true });

        if (!context) {
          reject(new Error("No se pudo preparar la muestra de fondo."));
          return;
        }

        context.drawImage(image, 0, 0, sampleSize, sampleSize);
        const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
        const samplePoints = [
          [1, 1],
          [sampleSize - 2, 1],
          [1, sampleSize - 2],
          [sampleSize - 2, sampleSize - 2],
          [Math.floor(sampleSize / 2), 1],
          [Math.floor(sampleSize / 2), sampleSize - 2],
        ];

        let red = 0;
        let green = 0;
        let blue = 0;
        let count = 0;

        samplePoints.forEach(([x, y]) => {
          const index = (y * sampleSize + x) * 4;
          const alpha = data[index + 3] / 255;

          if (alpha < 0.02) {
            return;
          }

          red += data[index];
          green += data[index + 1];
          blue += data[index + 2];
          count += 1;
        });

        if (!count) {
          resolve("#f8fafc");
          return;
        }

        resolve(
          rgbToHex({
            r: red / count,
            g: green / count,
            b: blue / count,
          })
        );
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => reject(new Error("No se pudo leer el logo seleccionado."));
    image.src = imageUrl;
  });
